import React, { useEffect } from 'react';
import { Fab, ListItem, ListItemText } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useToggle } from 'react-use';
import {
  ActivityIndicator,
  BottomSheet,
  EmptyState,
  FilePicker,
  List,
} from 'components';
import { useTranslation } from 'hooks';
import { useFiles, useFileTypes, useUploadFiles } from 'hooks/api/cascade';
import { useAbortRequest } from 'utils/useAbortRequest';
import { FilesList } from './components';
import styles from './FilesScreen.module.css';

const FilesScreen = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { entityId } = useParams();
  const { abortSignal } = useAbortRequest();
  const {
    status,
    isError,
    error,
    data: files,
  } = useFiles(entityId, abortSignal);
  const { data: fileTypes } = useFileTypes();
  const [isBottomSheetOpened, toggleBottomSheet] = useToggle(false);
  const { mutate } = useUploadFiles(entityId);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFileTypeSwitch = () => {
    toggleBottomSheet(true);
  };

  const handleFileUpload = (fileTypeId) => async (uploadFiles) => {
    await mutate({ fileType: fileTypeId, files: uploadFiles });
    toggleBottomSheet(false);
  };

  const renderFiles = () => {
    const hasFiles = files.length !== 0;

    if (!hasFiles) {
      return <EmptyState title={t('noFilesByCurrentCustomer')} />;
    }

    return <FilesList files={files} />;
  };

  const renderFetchState = () => {
    switch (status) {
      case 'loading':
        return <ActivityIndicator />;
      case 'success':
        return renderFiles();
      default:
        return null;
    }
  };

  return (
    <>
      {renderFetchState()}

      <Fab
        color="primary"
        aria-label={t('createNewPromise')}
        className="fab"
        onClick={openFileTypeSwitch}
      >
        <AddIcon />
      </Fab>

      <BottomSheet open={isBottomSheetOpened} onClose={toggleBottomSheet}>
        <List>
          {fileTypes &&
            fileTypes.map((fileType) => (
              <FilePicker
                key={fileType.id}
                className={styles.filePicker}
                onDrop={handleFileUpload(fileType.id)}
                maxFiles={5}
                multiple
              >
                <ListItem key={fileType.id} button>
                  <ListItemText primary={fileType.name} />
                </ListItem>
              </FilePicker>
            ))}
        </List>
      </BottomSheet>
    </>
  );
};

export default FilesScreen;
